'use client';

import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Share as ShareIcon,
  Cookie as CookieIcon,
  Gavel as GavelIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import Link from 'next/link';
import { AppHeader } from '@/components/layout/AppHeader';

const LAST_UPDATED = 'January 27, 2026';
const COMPANY_NAME = 'Mengir';
const COMPANY_EMAIL = 'privacy@mengir.me';

export default function PrivacyPolicyPage() {
  const theme = useTheme();

  const sections = [
    {
      id: 'information-we-collect',
      title: '1. Information We Collect',
      icon: <StorageIcon />,
      content: (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            1.1 Information You Provide
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Account Information"
                secondary="Name, email address, profile photo, and authentication credentials when you create an account."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Listing and Event Content"
                secondary="Business details, property information, event descriptions, photos, pricing, and contact information you provide when creating listings or events."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="AI-Generated Content"
                secondary="Descriptions and prompts you provide to our AI content generation features, along with the resulting generated content."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Communications"
                secondary="Messages sent through our platform, support tickets, and feedback you submit."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Payment Information"
                secondary="Billing details processed securely through Stripe. We do not store complete credit card numbers on our servers."
              />
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            1.2 Information Collected Automatically
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Device and Usage Data"
                secondary="IP address, browser type, device information, pages visited, and interactions with our platform."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Location Data"
                secondary="General location based on IP address to provide relevant local content and listings."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Cookies and Similar Technologies"
                secondary="Session cookies for authentication, preference cookies, and analytics cookies to improve our services."
              />
            </ListItem>
          </List>
        </>
      ),
    },
    {
      id: 'how-we-use',
      title: '2. How We Use Your Information',
      icon: <SecurityIcon />,
      content: (
        <List dense>
          <ListItem>
            <ListItemText
              primary="Provide and Improve Services"
              secondary="To operate the platform, process transactions, and enhance user experience."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="AI Content Generation"
              secondary="To generate listing and event content based on your descriptions. Your prompts may be used to improve our AI models, but we do not share identifiable information with AI providers."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Communications"
              secondary="To send service updates, respond to inquiries, and deliver notifications you've requested."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Safety and Security"
              secondary="To detect fraud, enforce our terms, and protect users and the platform."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Analytics"
              secondary="To understand usage patterns and improve our services."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Legal Compliance"
              secondary="To comply with applicable laws and legal obligations."
            />
          </ListItem>
        </List>
      ),
    },
    {
      id: 'information-sharing',
      title: '3. Information Sharing',
      icon: <ShareIcon />,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            We do not sell your personal information. We may share information in the following circumstances:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="With Your Consent"
                secondary="When you explicitly authorize sharing, such as when contacting a listing owner."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Service Providers"
                secondary="With trusted partners who help operate our platform (e.g., Stripe for payments, AWS for hosting, OpenAI for AI features). These providers are bound by confidentiality agreements."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Public Content"
                secondary="Listings, events, and community posts you create are publicly visible to all users."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Legal Requirements"
                secondary="When required by law, legal process, or to protect rights, safety, or property."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Business Transfers"
                secondary="In connection with a merger, acquisition, or sale of assets, with appropriate notice."
              />
            </ListItem>
          </List>
        </>
      ),
    },
    {
      id: 'cookies',
      title: '4. Cookies and Tracking',
      icon: <CookieIcon />,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            We use cookies and similar technologies to:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Essential Cookies"
                secondary="Required for authentication, security, and basic platform functionality."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Preference Cookies"
                secondary="Remember your settings, language preferences, and theme choices."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Analytics Cookies"
                secondary="Help us understand how users interact with our platform to improve services."
              />
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ mt: 2 }}>
            You can manage cookie preferences through your browser settings. Disabling certain cookies may affect platform functionality.
          </Typography>
        </>
      ),
    },
    {
      id: 'your-rights',
      title: '5. Your Rights and Choices',
      icon: <GavelIcon />,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Depending on your location, you may have the following rights:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Access and Portability"
                secondary="Request a copy of your personal data in a portable format."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Correction"
                secondary="Update or correct inaccurate personal information."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Deletion"
                secondary="Request deletion of your personal data, subject to legal retention requirements."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Opt-Out"
                secondary="Unsubscribe from marketing communications at any time."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Restrict Processing"
                secondary="Request limitations on how we use your data in certain circumstances."
              />
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ mt: 2 }}>
            To exercise these rights, contact us at{' '}
            <Link href={`mailto:${COMPANY_EMAIL}`} style={{ color: theme.palette.primary.main }}>
              {COMPANY_EMAIL}
            </Link>
            . We will respond within 30 days.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            California Residents (CCPA)
          </Typography>
          <Typography variant="body1">
            California residents have additional rights including the right to know what personal information is collected,
            the right to delete, and the right to opt-out of the sale of personal information. We do not sell personal information.
          </Typography>
        </>
      ),
    },
    {
      id: 'data-security',
      title: '6. Data Security and Retention',
      icon: <SecurityIcon />,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            We implement industry-standard security measures to protect your information:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Encryption"
                secondary="Data is encrypted in transit (TLS/SSL) and at rest."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Access Controls"
                secondary="Strict access controls and authentication for our systems."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Regular Audits"
                secondary="Periodic security assessments and monitoring."
              />
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ mt: 2 }}>
            We retain your information for as long as your account is active or as needed to provide services.
            After account deletion, we may retain certain data for legal compliance, fraud prevention, or legitimate business purposes for up to 3 years.
          </Typography>
        </>
      ),
    },
    {
      id: 'contact',
      title: '7. Contact Us',
      icon: <EmailIcon />,
      content: (
        <>
          <Typography variant="body1">
            For privacy-related questions or to exercise your rights, contact us:
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {COMPANY_NAME} Privacy Team
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email:{' '}
              <Link href={`mailto:${COMPANY_EMAIL}`} style={{ color: theme.palette.primary.main }}>
                {COMPANY_EMAIL}
              </Link>
            </Typography>
          </Box>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppHeader authenticated={false} />

      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
        {/* Back Button */}
        <Button
          component={Link}
          href="/"
          startIcon={<ArrowBackIcon />}
          sx={{
            textTransform: 'none',
            mb: 3,
            minHeight: 44,
            fontWeight: 600,
            '&:active': { bgcolor: 'action.selected' },
          }}
        >
          Back
        </Button>

        {/* Header */}
        <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, mb: 3 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.5rem', sm: '2rem' } }}
          >
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Last updated: {LAST_UPDATED}
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            At {COMPANY_NAME}, we are committed to protecting your privacy and being transparent about how we
            collect, use, and share your information. This Privacy Policy explains our practices for the
            Mengir platform, which connects Ethiopian and Eritrean communities through business listings,
            property sales, events, and community features.
          </Typography>
        </Paper>

        {/* Table of Contents */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Contents
          </Typography>
          <List dense disablePadding>
            {sections.map((section) => (
              <ListItem
                key={section.id}
                component={Link}
                href={`#${section.id}`}
                sx={{
                  py: 1,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{section.icon}</ListItemIcon>
                <ListItemText
                  primary={section.title}
                  primaryTypographyProps={{ fontSize: '0.95rem' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Sections */}
        {sections.map((section, index) => (
          <Paper
            key={section.id}
            id={section.id}
            sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, mb: 3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ color: 'primary.main' }}>{section.icon}</Box>
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                {section.title}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {section.content}
          </Paper>
        ))}

        {/* Changes Notice */}
        <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, bgcolor: 'action.hover' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Changes to This Policy
          </Typography>
          <Typography variant="body1">
            We may update this Privacy Policy periodically. We will notify you of significant changes
            by posting a notice on our platform or sending an email. Your continued use of Mengir after
            changes constitutes acceptance of the updated policy.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
